import { enumMembers, type ApiSchema } from "./apiSchema";
import { jsonBody, requestJson } from "./http/httpClient";
import { readStringList } from "./content/contentDecoders";
import { readArray, readBoolean, readObject, readOneOf, readString, type JsonObject } from "./http/jsonDecoding";

/**
 * The signed-in user's own settings, kept with their account (accounts.md, As built: each
 * user's view settings): the same on every device. A change names only the settings it
 * changes. Besides views, what becomes of the memories their agents save for them alone
 * (ADR-0031 D5, "user settings").
 */

export type WorkflowGraphDensity = ApiSchema<"WorkflowGraphDensity">;

export const WORKFLOW_GRAPH_DENSITIES = enumMembers<WorkflowGraphDensity>({ compact: true, standard: true, detailed: true });

export type WorkflowGraphLayout = ApiSchema<"WorkflowGraphLayout">;

export const WORKFLOW_GRAPH_LAYOUTS = enumMembers<WorkflowGraphLayout>({ horizontal: true, vertical: true });

export type PersonalMemoryPolicy = ApiSchema<"PersonalMemoryPolicy">;

export const PERSONAL_MEMORY_POLICIES = enumMembers<PersonalMemoryPolicy>({ keep: true, confirm: true, off: true });

export type AutoSettleAfter = ApiSchema<"AutoSettleAfter">;

/** As the choice shows them: off, then shortest first. */
export const AUTO_SETTLE_CHOICES = enumMembers<AutoSettleAfter>({ off: true, one_day: true, three_days: true, one_week: true, one_month: true });

/**
 * An LLM starred in the LLM picker (F106): by its route, an agent's ID or an API provider's
 * host, and its model; never by PC, so the star follows the person to every PC with it.
 */
export interface StarredLlm {
  readonly routeKind: "agent" | "api_key";
  readonly route: string;
  readonly modelId: string;
}

export interface UserPreferences {
  readonly workflowGraphDensity: WorkflowGraphDensity;
  readonly personalMemoryPolicy: PersonalMemoryPolicy;
  readonly workflowGraphLayout: WorkflowGraphLayout;
  /** Whether help opens in its own tab, or in place of the page (web-app.md WA-6). */
  readonly openHelpInNewTab: boolean;
  /** After how long without activity a Thread settles itself in the person's list (F69). */
  readonly autoSettleAfter: AutoSettleAfter;
  /** The Workflows the person starred, by item ID: every version of each (F97). */
  readonly favouriteWorkflows: ReadonlySet<string>;
  /** In the order starred. */
  readonly starredLlms: readonly StarredLlm[];
  /** The person's reasoning level per agent ID, for Runs left at "Standard": one of the levels the agent's PCs report. */
  readonly reasoningLevels: Readonly<Record<string, string>>;
  /** The installation's level per agent ID, read-only here: "Standard" for an agent the person set none for. */
  readonly installationReasoningLevels: Readonly<Record<string, string>>;
}

/** Set one agent's reasoning level, or with `effortId` `null` go back to the installation's; the other agents' stay. */
export interface ReasoningLevelChange {
  readonly agentId: string;
  readonly effortId: string | null;
}

/** Star or unstar one Workflow, by item ID; the other stars stay. */
export interface FavouriteWorkflowChange {
  readonly workflow: string;
  readonly favourite: boolean;
}

/** Star or unstar one LLM; the other stars stay, and a new one goes after them (BA16-04). */
export interface StarredLlmChange {
  readonly llm: StarredLlm;
  readonly starred: boolean;
}

/** Whether two stars name the same LLM: by its route and model, never by PC. */
export function sameStarredLlm(left: StarredLlm, right: StarredLlm): boolean {
  return left.routeKind === right.routeKind && left.route === right.route && left.modelId === right.modelId;
}

/** The settings to change; stars change one Workflow or LLM at a time, so two devices never undo each other's. */
export type UserPreferencesChange = Partial<Omit<UserPreferences, "favouriteWorkflows" | "starredLlms" | "reasoningLevels" | "installationReasoningLevels">> & { readonly favouriteWorkflow?: FavouriteWorkflowChange; readonly starredLlm?: StarredLlmChange; readonly reasoningLevel?: ReasoningLevelChange };

/** What an account without stored settings reads as; the server's defaults. The installation's levels are the server's to say. */
export const DEFAULT_USER_PREFERENCES: UserPreferences = { workflowGraphDensity: "standard", workflowGraphLayout: "horizontal", personalMemoryPolicy: "off", openHelpInNewTab: true, autoSettleAfter: "off", favouriteWorkflows: new Set(), starredLlms: [], reasoningLevels: {}, installationReasoningLevels: {} };

function decodeLevels(preferences: JsonObject, member: string): Readonly<Record<string, string>> {
  const levels = readArray(preferences[member] ?? [], "reasoning levels", (item) => {
    const level = readObject(item, "reasoning level");
    return [readString(level, "agent_id"), readString(level, "effort_id")] as const;
  });
  return Object.fromEntries(levels);
}

const PATH = "/accounts/users/me/preferences";

function decodePreferences(body: unknown): UserPreferences {
  const preferences = readObject(body, "view settings");
  return {
    workflowGraphDensity: readOneOf(preferences, "workflow_graph_density", WORKFLOW_GRAPH_DENSITIES),
    personalMemoryPolicy: readOneOf(preferences, "personal_memory_policy", PERSONAL_MEMORY_POLICIES),
    workflowGraphLayout: readOneOf(preferences, "workflow_graph_layout", WORKFLOW_GRAPH_LAYOUTS),
    openHelpInNewTab: readBoolean(preferences, "open_help_in_new_tab"),
    autoSettleAfter: readOneOf(preferences, "auto_settle_after", AUTO_SETTLE_CHOICES),
    favouriteWorkflows: new Set(readStringList(preferences, "favourite_workflows")),
    starredLlms: readArray(preferences["starred_llms"] ?? [], "starred LLMs", (item) => {
      const star = readObject(item, "starred LLM");
      return { routeKind: readOneOf(star, "route_kind", ["agent", "api_key"] as const), route: readString(star, "route"), modelId: readString(star, "model_id") };
    }),
    reasoningLevels: decodeLevels(preferences, "reasoning_levels"),
    installationReasoningLevels: decodeLevels(preferences, "installation_reasoning_levels"),
  };
}

export function readUserPreferences(signal?: AbortSignal): Promise<UserPreferences> {
  return requestJson({ method: "GET", path: PATH, signal, decodeResponse: decodePreferences });
}

export function changeUserPreferences(change: UserPreferencesChange): Promise<UserPreferences> {
  const body: ApiSchema<"UserPreferencesChangeBody"> = {
    ...(change.workflowGraphDensity === undefined ? {} : { workflow_graph_density: change.workflowGraphDensity }),
    ...(change.personalMemoryPolicy === undefined ? {} : { personal_memory_policy: change.personalMemoryPolicy }),
    ...(change.workflowGraphLayout === undefined ? {} : { workflow_graph_layout: change.workflowGraphLayout }),
    ...(change.openHelpInNewTab === undefined ? {} : { open_help_in_new_tab: change.openHelpInNewTab }),
    ...(change.autoSettleAfter === undefined ? {} : { auto_settle_after: change.autoSettleAfter }),
    ...(change.favouriteWorkflow === undefined ? {} : { favourite_workflow: change.favouriteWorkflow }),
    ...(change.starredLlm === undefined ? {} : { starred_llm: { llm: { route_kind: change.starredLlm.llm.routeKind, route: change.starredLlm.llm.route, model_id: change.starredLlm.llm.modelId }, starred: change.starredLlm.starred } }),
    ...(change.reasoningLevel === undefined ? {} : { reasoning_level: { agent_id: change.reasoningLevel.agentId, effort_id: change.reasoningLevel.effortId } }),
  };
  return requestJson({ method: "PATCH", path: PATH, body: jsonBody(body), decodeResponse: decodePreferences });
}
